/**
 * MarginBreakdown — Deckungsbeitrag nach Schichten.
 *
 * The drawer table of a Deckungsbeitrag row, organised by how a cost BEHAVES
 * (ertragsabhängig je kg · ertragsunabhängig je ha · Investition · Absatz) rather
 * than by account, with one number column that follows a Bezugsgröße switcher
 * (€ gesamt / je ha / je kg / je lfm / je Pflanze), the price floors as lines
 * under their subtotal, and the selected row's Rechenweg in a permanently
 * reserved panel on the right — the table never reflows when a row opens.
 *
 * Controlled and presentational: the consumer owns basis, view and selection and
 * supplies the open row's detail; the organism derives subtotals and per-basis
 * values from the euro totals it is given.
 *
 * No share bar beside the number (dropped 2026-09-07): farmers did not understand
 * a bar a few pixels wide next to the figure, and the figure is the point.
 */
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import type {
  MarginBreakdownBlock,
  MarginBreakdownFloor,
  MarginBreakdownProps,
  MarginBreakdownRow,
} from './types';
import { formatInBasis } from './format';
import { MarginBreakdownDetail } from './MarginBreakdownDetail';
import './MarginBreakdown.css';

const DEFAULT_EMPTY = 'Zeile anklicken für Rechenweg und Belege.';

interface SubtotalModel {
  block: MarginBreakdownBlock;
  running: number;
}

export const MarginBreakdown: React.FC<MarginBreakdownProps> = ({
  title,
  meta,
  leistung,
  leistungHeading = 'Erlöse',
  headerContainer = null,
  blocks,
  basisOptions,
  basis,
  onBasisChange,
  viewOptions,
  view,
  onViewChange,
  selectedRowId = null,
  onSelectRow,
  detail,
  panelFooter,
  emptyDetailText = DEFAULT_EMPTY,
  locale = 'de-DE',
  className = '',
}) => {
  const option = basisOptions.find((o) => o.id === basis);

  // Cumulative subtotals: Leistung plus every row value up to and including the block.
  const subtotals = useMemo(() => {
    let running = leistung.value ?? 0;
    const out = new Map<string, SubtotalModel>();
    for (const block of blocks) {
      for (const row of block.rows) {
        // #4172 — a „davon" subline's euro is already inside its parent row's, so adding it
        // here would subtract the same money twice from every subtotal below.
        if (row.subline) continue;
        if (row.value !== null && row.value !== undefined) running += row.value;
      }
      if (block.subtotal) out.set(block.id, { block, running });
    }
    return out;
  }, [blocks, leistung.value]);

  const select = (rowId: string) => onSelectRow?.(selectedRowId === rowId ? null : rowId);

  const renderRow = (row: MarginBreakdownRow) => {
    const selected = selectedRowId === row.id;
    const formatted = formatInBasis(row.value, option, locale);
    const disabled = Boolean(row.disabled);
    // A 0 ROW with no flag is captioned "Keine Erfassung" beside its label; a 0 row whose flag
    // already names the reason ("Preis fehlt · 2 Buchungen") is not — the two would contradict
    // each other — and a 0 SUBTOTAL never is: that zero is arithmetic, not a missing record.
    const captionZero = row.value === 0 && !row.flag;
    return (
      <tr
        key={row.id}
        className={`margin-breakdown__row${row.subline ? ' margin-breakdown__row--subline' : ''}`}
        data-selected={selected || undefined}
        data-disabled={disabled || undefined}
        data-subline={row.subline || undefined}
      >
        <td>
          <button
            type="button"
            className="margin-breakdown__rowbutton"
            aria-pressed={selected}
            // Inert, not `disabled`: a disabled button is unfocusable, so the reason in its
            // title would never reach a keyboard or screen-reader user. The click guard below
            // is what makes it inert (HOF-467 follow-up, found by codex).
            aria-disabled={disabled || undefined}
            title={row.title}
            onClick={() => {
              if (!disabled) select(row.id);
            }}
          >
            <span className="margin-breakdown__label">{row.label}</span>
            {row.flag && (
              <span
                className={`margin-breakdown__flag margin-breakdown__flag--${row.flag.tone ?? 'neutral'}`}
                title={row.flag.title}
              >
                {row.flag.label}
              </span>
            )}
            {captionZero && <span className="margin-breakdown__flag margin-breakdown__flag--neutral">Keine Erfassung</span>}
            <span className="margin-breakdown__leader" aria-hidden="true" />
          </button>
        </td>
        <td className="margin-breakdown__num">
          {formatted === null ? <span className="margin-breakdown__pending">—</span> : formatted}
        </td>
      </tr>
    );
  };

  const renderFloor = (floor: MarginBreakdownFloor, i: number) => (
    <tr key={`${floor.label}-${i}`} className="margin-breakdown__floorrow">
      <td colSpan={2}>
        <div className="margin-breakdown__floor">
          <span>
            <span className="margin-breakdown__floor-label">{floor.label}</span>{' '}
            <b>{floor.value}</b>
          </span>
          {floor.achieved && (
            <span>
              <span className="margin-breakdown__floor-label">{floor.achieved.label}</span>{' '}
              <b>{floor.achieved.value}</b>
            </span>
          )}
          {floor.verdict && (
            <span className={`margin-breakdown__verdict margin-breakdown__verdict--${floor.verdict.tone}`}>
              {floor.verdict.label}
            </span>
          )}
        </div>
      </td>
    </tr>
  );

  const renderSubtotal = (block: MarginBreakdownBlock) => {
    const sub = block.subtotal;
    if (!sub) return null;
    const model = subtotals.get(block.id);
    // The persisted figure wins over the running sum (HOF-467) — see MarginBreakdownSubtotal.value.
    const running = sub.value ?? (model ? model.running : 0);
    const pending = sub.pending || !option || option.divisor === null;
    const formatted = pending ? null : formatInBasis(running, option, locale);
    const tone = running >= 0 ? 'positive' : 'negative';
    return (
      <React.Fragment key={`${block.id}-subtotal`}>
        <tr className={`margin-breakdown__subtotal${sub.final ? ' margin-breakdown__subtotal--final' : ''}`}>
          <td>
            {/* HOF-512 — an inner flex container, because `white-space` alone cannot wrap
                these. JSX strips the whitespace between adjacent elements, so the label and
                the flag pills are inline boxes with NO soft-wrap opportunity between them:
                the cell may permit wrapping and the row still refuses to break. Flexing the
                `td` itself would wrap them, and would also take the cell out of the table's
                column layout that the leader dots and the numeric column are aligned by —
                so the flex box goes inside the cell instead. */}
            <span className="margin-breakdown__subtotal-head">
              <span className="margin-breakdown__label">{sub.label}</span>
              {sub.flags?.map((flag, i) => (
                <span
                  key={`${flag.label}-${i}`}
                  className={`margin-breakdown__flag margin-breakdown__flag--${flag.tone ?? 'neutral'}`}
                  title={flag.title}
                >
                  {flag.label}
                </span>
              ))}
            </span>
          </td>
          <td className="margin-breakdown__num">
            {formatted === null ? (
              <span className="margin-breakdown__pending">—</span>
            ) : (
              <span className={`margin-breakdown__value margin-breakdown__value--${tone}`}>{formatted}</span>
            )}
          </td>
        </tr>
        {!sub.pending && sub.floors?.map(renderFloor)}
      </React.Fragment>
    );
  };

  /* One row: the meta line (the divisors — ha · kg · lfm · Pflanzen) on the left, the
     switchers that divide by them on the right; they wrap under it on a narrow sheet.
     Portaled into `headerContainer` when the consumer has a title row to put it on. */
  const header = (
    <header className={`margin-breakdown__head${headerContainer ? ' margin-breakdown__head--portaled' : ''}`}>
        {(title || (meta && meta.length > 0)) && (
          <div className="margin-breakdown__ident">
            {title && <h2 className="margin-breakdown__title">{title}</h2>}
            {meta && meta.length > 0 && <p className="margin-breakdown__meta">{meta.join(' · ')}</p>}
          </div>
        )}
        <div className="margin-breakdown__controls">
          <div className="margin-breakdown__seg" role="group" aria-label="Bezugsgröße">
          {basisOptions.map((o) => (
            <button
              key={o.id}
              type="button"
              aria-pressed={o.id === basis}
              disabled={o.divisor === null}
              title={o.divisor === null ? o.disabledReason : undefined}
              onClick={() => onBasisChange(o.id)}
            >
              {o.label}
            </button>
          ))}
          </div>
          {viewOptions && viewOptions.length > 0 && (
            <div className="margin-breakdown__seg" role="group" aria-label="Gliederung">
              {viewOptions.map((v) => (
                <button key={v.id} type="button" aria-pressed={v.id === view} onClick={() => onViewChange?.(v.id)}>
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>
  );

  return (
    <section className={`margin-breakdown ${className}`.trim()}>
      {headerContainer ? createPortal(header, headerContainer) : header}

      <div className="margin-breakdown__body">
        <div className="margin-breakdown__main">
          <table className="margin-breakdown__table" aria-label="So setzt sich das zusammen">
            <colgroup>
              <col className="margin-breakdown__col-label" />
              <col className="margin-breakdown__col-num" />
            </colgroup>
            <thead className="margin-breakdown__sr-only">
              <tr>
                <th scope="col">Position</th>
                <th scope="col">{option?.unit || '€'}</th>
              </tr>
            </thead>
            <tbody>
              {leistungHeading && (
                <tr className="margin-breakdown__block">
                  <td colSpan={2}>{leistungHeading}</td>
                </tr>
              )}
              {renderRow(leistung)}
              {leistung.yieldLine && (
                <tr className="margin-breakdown__noterow">
                  <td colSpan={2}>{leistung.yieldLine}</td>
                </tr>
              )}
              {blocks.map((block) => (
                <React.Fragment key={block.id}>
                  <tr className="margin-breakdown__block">
                    <td colSpan={2}>{block.title}</td>
                  </tr>
                  {block.rows.map((row) => renderRow(row))}
                  {renderSubtotal(block)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <aside className="margin-breakdown__panel" aria-label="Rechenweg" aria-live="polite">
          {/* ONE sticky scroll layer for the whole column, and that is the fix rather than a
              wrapper for its own sake. The sticky rule used to be keyed on
              ``.margin-breakdown__panel > *`` — correct only while the panel had a single
              child. ``panelFooter`` (HOF-512) made that two, both pinned to the same
              ``top``, so the provenance block painted over the open line's Belege.

              The panel itself cannot take the rule: it is a grid item stretched to the
              table's height, which is what draws the column's full-height ``border-left``,
              and a sticky box has to be able to be shorter than its track. So the stretch
              (and the border) stays on the aside, and the pinning moves one level in — to a
              box that holds every block, whatever their number. */}
          <div className="margin-breakdown__panel-scroll">
            <MarginBreakdownDetail
              detail={detail}
              basisOption={option}
              locale={locale}
              emptyText={emptyDetailText}
              onClose={onSelectRow ? () => onSelectRow(null) : undefined}
            />
            {panelFooter}
          </div>
        </aside>
      </div>
    </section>
  );
};
